import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, X } from 'lucide-react';

export interface DesvioSubmitPayload {
  justificativa: string;
  gerarAcao: boolean;
  indicatorRealizedValue?: number | null;
}

interface DesvioModalProps {
  open: boolean;
  rotinaTitulo: string;
  /** Justificativa pré-existente (edição de um desvio já registrado). */
  initialJustificativa?: string;
  /** Indicador vinculado — exige valor realizado. */
  indicatorName?: string | null;
  indicatorTarget?: number | null;
  indicatorUnit?: string;
  initialIndicatorValue?: number | null;
  saving?: boolean;
  onClose: () => void;
  onSubmit: (payload: DesvioSubmitPayload) => void;
}

/**
 * Pop-up minimalista para registrar o motivo de um desvio e, opcionalmente,
 * disparar uma pendência na Central de Ações.
 */
export const DesvioModal: React.FC<DesvioModalProps> = ({
  open,
  rotinaTitulo,
  initialJustificativa = '',
  indicatorName = null,
  indicatorTarget = null,
  indicatorUnit = '',
  initialIndicatorValue = null,
  saving = false,
  onClose,
  onSubmit,
}) => {
  const [justificativa, setJustificativa] = useState(initialJustificativa);
  const [gerarAcao, setGerarAcao] = useState(false);
  const [indicatorValor, setIndicatorValor] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const requiresIndicator = !!indicatorName;

  useEffect(() => {
    if (open) {
      setJustificativa(initialJustificativa);
      setGerarAcao(false);
      setIndicatorValor(
        initialIndicatorValue != null && Number.isFinite(initialIndicatorValue)
          ? String(initialIndicatorValue)
          : '',
      );
      const t = setTimeout(() => textareaRef.current?.focus(), 40);
      return () => clearTimeout(t);
    }
  }, [open, initialJustificativa, initialIndicatorValue]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const parsedIndicator = Number(indicatorValor.replace(',', '.'));
  const indicatorOk = !requiresIndicator || Number.isFinite(parsedIndicator);
  const canSubmit = justificativa.trim().length >= 3 && indicatorOk && !saving;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-slate-900/45 backdrop-blur-[1px]"
        onClick={saving ? undefined : onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Registrar desvio"
        className="relative w-full max-w-[420px] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-[var(--bg-surface)]"
      >
        <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3 dark:border-slate-700/60">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400">
            <AlertTriangle size={15} strokeWidth={2} />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-[13px] font-bold text-slate-800 dark:text-slate-100">Registrar desvio</h2>
            <p className="truncate text-[11px] text-slate-500 dark:text-slate-400">{rotinaTitulo}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 disabled:opacity-40 dark:hover:bg-slate-800"
            aria-label="Fechar"
          >
            <X size={15} />
          </button>
        </div>

        <div className="px-4 py-3.5">
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Motivo do desvio
          </label>
          <textarea
            ref={textareaRef}
            value={justificativa}
            onChange={(e) => setJustificativa(e.target.value)}
            rows={3}
            placeholder="Descreva o que impediu a execução padrão da rotina…"
            className="w-full resize-none rounded-md border border-slate-200 px-3 py-2 text-[12px] leading-relaxed text-slate-700 outline-none transition-colors placeholder:text-slate-400 focus:border-slate-500 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-200"
          />

          {requiresIndicator && (
            <div className="mt-3">
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Informe o valor Realizado <span className="text-red-500">*</span>
              </label>
              <p className="mb-1 text-[10px] text-slate-400">
                {indicatorName}
                {indicatorTarget != null ? ` · Meta: ${indicatorTarget}${indicatorUnit}` : ''}
              </p>
              <input
                type="number"
                step="any"
                value={indicatorValor}
                onChange={(e) => setIndicatorValor(e.target.value)}
                className="h-8 w-full rounded-md border border-slate-200 px-3 text-[12px] tabular-nums text-slate-700 outline-none focus:border-slate-500 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-200"
              />
            </div>
          )}

          <label className="mt-3 flex cursor-pointer items-start gap-2 rounded-md border border-slate-200 px-3 py-2 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800/40">
            <input
              type="checkbox"
              checked={gerarAcao}
              onChange={(e) => setGerarAcao(e.target.checked)}
              className="mt-0.5 h-3.5 w-3.5 accent-blue-600"
            />
            <span className="text-[11px] leading-snug text-slate-600 dark:text-slate-300">
              Gerar pendência na <strong className="font-semibold text-slate-800 dark:text-slate-100">Central de Ações</strong> para tratar este desvio.
            </span>
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-4 py-3 dark:border-slate-700/60">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-md px-3 py-1.5 text-[12px] font-medium text-slate-600 transition-colors hover:bg-slate-100 disabled:opacity-40 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() =>
              onSubmit({
                justificativa: justificativa.trim(),
                gerarAcao,
                indicatorRealizedValue: requiresIndicator ? parsedIndicator : null,
              })
            }
            disabled={!canSubmit}
            className="rounded-md bg-amber-600 px-3.5 py-1.5 text-[12px] font-semibold text-white shadow-sm transition-[filter,opacity] hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {saving ? 'Salvando…' : 'Confirmar desvio'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default DesvioModal;
